// LoginPage — Split-screen design alineado a la guía visual.
// Panel izquierdo con gradiente de la marca activa + logo grande + tagline.
// Panel derecho con selector de empresa (Tecno/Freeo) + form Ant Design.

import React, { useState, useEffect } from 'react';
import { Form, Input, Button, message, Alert } from 'antd';
import { UserOutlined, LockOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useBrand } from '../../context/BrandContext';
import BrandMark from '../common/BrandMark';

const LoginPage = () => {
    const [loading, setLoading] = useState(false);
    const [loginError, setLoginError] = useState(null);
    const [notification, setNotification] = useState(null);
    const navigate = useNavigate();
    const { login } = useAuth();
    const { brandKey, brand, brands, setBrandKey } = useBrand();

    const isGeneric = brandKey === 'generic';

    useEffect(() => {
        const reason = sessionStorage.getItem('logoutReason');
        if (reason === 'inactivity') {
            setNotification({
                title: 'Sesión finalizada por inactividad.',
                message: 'La sesión se cerró automáticamente después de un periodo de inactividad. Ingresa tus credenciales para volver a entrar.',
            });
            sessionStorage.removeItem('logoutReason');
        }
    }, []);

    const onFinish = async (values) => {
        setLoading(true);
        setLoginError(null);
        try {
            // companyKey = brandKey: la marca elegida en el panel determina a qué
            // empresa SAP/Mongo accede el usuario durante toda la sesión.
            const success = await login(values.username, values.password, brandKey);
            if (success) {
                message.success('Inicio de sesión exitoso!');
                navigate('/');
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Error en el inicio de sesión. Verifique sus credenciales.';
            setLoginError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleFormChange = () => { if (loginError) setLoginError(null); };

    const leftBackground = isGeneric
        ? 'linear-gradient(135deg, #1a2540 0%, #2a3a5e 100%)'
        : `linear-gradient(135deg, ${brand.primaryDeep} 0%, ${brand.primary} 100%)`;

    return (
        <div className="login-shell">
            {/* Panel izquierdo — branded */}
            <div className="login-brand-panel" style={{ background: leftBackground }}>
                <div className="login-brand-circle-1" style={{ borderColor: `${brand.accent}30` }} />
                <div className="login-brand-circle-2" style={{ background: `${brand.accent}25` }} />

                <div className="login-brand-logo-wrap" style={{
                    background: !isGeneric ? '#fff' : 'transparent',
                    padding: !isGeneric ? '10px 14px' : 0,
                }}>
                    <BrandMark size={36} dark={isGeneric} />
                </div>

                <div className="login-brand-copy">
                    <div className="login-brand-title">Sistema de Gestión Optimus</div>
                    <div className="login-brand-sub">
                        {isGeneric
                            ? 'Selecciona tu empresa para continuar. Empaque, entregas y trazabilidad integrado con SAP Business One.'
                            : `Acceso para personal de ${brand.name}. Empaque, entregas y trazabilidad integrado con SAP Business One.`}
                    </div>
                </div>

                <div className="login-brand-foot">v2.4.1 · © {new Date().getFullYear()}</div>
            </div>

            {/* Panel derecho — formulario */}
            <div className="login-form-panel">
                <div className="login-form-inner">
                    <div className="login-form-eyebrow">Iniciar sesión</div>
                    <h2 className="login-form-h2">Bienvenido de vuelta</h2>

                    <div className="login-form-label">Empresa</div>
                    <div className="login-brand-grid">
                        {Object.values(brands).filter(b => b.key !== 'generic').map(b => {
                            const active = brandKey === b.key;
                            return (
                                <div
                                    key={b.key}
                                    className={`login-brand-tile ${active ? 'active' : ''}`}
                                    onClick={() => setBrandKey(b.key)}
                                    style={active ? {
                                        borderColor: b.primary,
                                        background: b.primarySoft,
                                    } : {}}
                                >
                                    {active && (
                                        <span className="login-brand-tile-check" style={{ color: b.primary }}>✓</span>
                                    )}
                                    {b.logo && <img src={b.logo} alt={b.name} className="login-brand-tile-logo" />}
                                    <span
                                        className="login-brand-tile-name"
                                        style={{ color: active ? b.primary : '#5e6779' }}
                                    >
                                        {b.name}
                                    </span>
                                </div>
                            );
                        })}
                    </div>

                    {notification && (
                        <Alert
                            message={notification.title}
                            description={notification.message}
                            type="info" showIcon icon={<InfoCircleOutlined />} closable
                            onClose={() => setNotification(null)}
                            style={{ marginBottom: 14 }}
                            className="inactivity-alert"
                        />
                    )}

                    {loginError && (
                        <Alert
                            message="Error de autenticación"
                            description={loginError}
                            type="error" showIcon closable
                            onClose={() => setLoginError(null)}
                            style={{ marginBottom: 14 }}
                        />
                    )}

                    <Form
                        name="login_form"
                        layout="vertical"
                        onFinish={onFinish}
                        onValuesChange={handleFormChange}
                        requiredMark={false}
                    >
                        <Form.Item
                            name="username"
                            label="Usuario"
                            rules={[{ required: true, message: 'Ingrese su usuario' }]}
                        >
                            <Input prefix={<UserOutlined style={{ color: '#9aa3bd' }} />} placeholder="usuario" autoComplete="username" />
                        </Form.Item>
                        <Form.Item
                            name="password"
                            label="Contraseña"
                            rules={[{ required: true, message: 'Ingrese su contraseña' }]}
                        >
                            <Input.Password prefix={<LockOutlined style={{ color: '#9aa3bd' }} />} placeholder="••••••••" autoComplete="current-password" />
                        </Form.Item>

                        <Form.Item style={{ marginBottom: 0, marginTop: 4 }}>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={loading}
                                disabled={isGeneric}
                                block
                                size="middle"
                                style={{ fontWeight: 600, height: 40 }}
                            >
                                {isGeneric ? 'Selecciona una empresa' : `Ingresar a ${brand.name}`}
                            </Button>
                        </Form.Item>
                    </Form>

                    <div className="login-form-foot">
                        ¿Olvidaste tu contraseña?
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
